import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import Chart from "react-chartjs-2";
import { orange, green, red } from "@mui/material/colors";

import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  Button as MuiButton,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableHead,
  TableRow as MuiTableRow,
  Typography,
} from "@mui/material";
import { Loop as LoopIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { getDoughnutChartData } from "src/services/generalService";

const Card = styled(MuiCard)(spacing);

const SmallButton = styled(MuiButton)`
  padding: 4px;
  min-width: 0;

  svg {
    width: 0.9em;
    height: 0.9em;
  }
`;

const ChartWrapper = styled.div`
  height: 168px;
  position: relative;
`;

const DoughnutInner = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 0;
  margin-top: -22px;
  text-align: center;
  z-index: 0;
`;

const TableRow = styled(MuiTableRow)`
  height: 42px;
`;

const TableCell = styled(MuiTableCell)`
  padding-top: 0;
  padding-bottom: 0;
`;

const GreenText = styled.span`
  color: ${() => green[400]};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const RedText = styled.span`
  color: ${() => red[400]};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const LoaderWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 168px;
`;

/**
 * Reusable Doughnut Chart component that fetches data from an API
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {string} props.apiUrl - API endpoint to fetch data from
 * @param {Object} props.theme - Theme object from withTheme HOC
 * @param {number} props.mb - Margin bottom from MUI spacing (default: 6)
 */
function DoughnutChartCard({
  title = "Chart",
  apiUrl,
  theme,
  mb = 6,
}) {
  const [chartData, setChartData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getDoughnutChartData(apiUrl);

      if (response?.status !== 200 || !response?.data) {
        throw new Error(
          response?.message || `API Error: ${response?.status || "Unknown"}`
        );
      }
      const data = response?.data;

      // Format chart data for doughnut chart
      const formattedChartData = {
        labels: data.labels || [],
        centerText: data.centerText || "+27%",
        centerCaption: data.centerCaption || "more sales",
        datasets: [
          {
            data: data.values || [],
            backgroundColor: data.backgroundColor || [
              theme.palette.secondary.main,
              red[500],
              orange[500],
              theme.palette.grey[200],
            ],
            borderWidth: 5,
            borderColor: theme.palette.background.paper,
          },
        ],
      };

      setChartData(formattedChartData);
      setTableData(data.tableData || []);
    } catch (err) {
      console.error("Error fetching doughnut chart data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (apiUrl) {
      fetchData();
    }
  }, [apiUrl, theme]);

  const defaultOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    cutout: "80%",
  };

  const isLoading = loading;

  return (
    <Card mb={mb}>
      <CardHeader
        action={
          <SmallButton
            size="small"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <LoopIcon />
          </SmallButton>
        }
        title={title}
      />
      <CardContent>
        {isLoading ? (
          <LoaderWrapper>
            <CircularProgress />
          </LoaderWrapper>
        ) : error ? (
          <LoaderWrapper>
            <div>Error loading chart: {error}</div>
          </LoaderWrapper>
        ) : chartData ? (
          <>
            <ChartWrapper>
              <DoughnutInner>
                <Typography variant="h4">{chartData.centerText || "+27%"}</Typography>
                <Typography variant="caption">
                  {chartData.centerCaption || "more sales"}
                </Typography>
              </DoughnutInner>
              <Chart type="doughnut" data={chartData} options={defaultOptions} />
            </ChartWrapper>
            {tableData.length > 0 && (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Source</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell component="th" scope="row">
                        {row.source}
                      </TableCell>
                      <TableCell align="right">{row.revenue}</TableCell>
                      <TableCell align="right">
                        {row.isPositive ? (
                          <GreenText>{row.value}</GreenText>
                        ) : (
                          <RedText>{row.value}</RedText>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default withTheme(DoughnutChartCard);
